import type { PaddingValues, BreakpointKey, BorderConfig, BorderSideConfig } from "../types";
import { BREAKPOINTS } from "../types";

// Default padding values
export const DEFAULT_PADDING: PaddingValues = { 
  top: "8", 
  right: "12", 
  bottom: "8", 
  left: "12" 
};

// Default border side config
export const DEFAULT_BORDER_SIDE: BorderSideConfig = {
  color: "#e5e7eb",
  width: "1",
};

// Default border config
export const DEFAULT_BORDER: BorderConfig = {
  top: { ...DEFAULT_BORDER_SIDE },
  right: { ...DEFAULT_BORDER_SIDE },
  bottom: { ...DEFAULT_BORDER_SIDE },
  left: { ...DEFAULT_BORDER_SIDE },
};

/**
 * Parse a style string into an object of style properties
 */
export const parseStyleString = (styleStr: string): Record<string, string> => {
  const styles: Record<string, string> = {};
  if (!styleStr) return styles;
  styleStr.split(";").forEach((rule) => {
    const [prop, val] = rule.split(":").map((s) => s.trim());
    if (prop && val) {
      styles[prop] = val;
    }
  });
  return styles;
};

/**
 * Extract numeric value from a CSS value string (e.g., "16px" -> "16")
 */
export const extractNumericValue = (value: string | number | undefined | null): string => {
  if (value === undefined || value === null) return "";
  // If it's already a number, convert to string
  if (typeof value === "number") return String(value);
  // If it's not a string, try to convert it
  if (typeof value !== "string") {
    try {
      value = String(value);
    } catch {
      return "";
    }
  }
  const match = value.match(/(\d+)/);
  return match ? match[1] : "";
};

/**
 * Padding value handlers for different lengths
 */
const PADDING_HANDLERS: Record<number, (values: string[]) => PaddingValues> = {
  1: (values) => ({ top: values[0], right: values[0], bottom: values[0], left: values[0] }),
  2: (values) => ({ top: values[0], right: values[1], bottom: values[0], left: values[1] }),
  3: (values) => ({ top: values[0], right: values[1], bottom: values[2], left: values[1] }),
  4: (values) => ({ top: values[0], right: values[1], bottom: values[2], left: values[3] }),
};

/**
 * Parse a padding string into PaddingValues object
 */
export const parsePaddingString = (paddingStr: string): PaddingValues => {
  if (!paddingStr) return { ...DEFAULT_PADDING };
  const values = paddingStr.split(" ").map((v) => extractNumericValue(v));
  const handler = PADDING_HANDLERS[values.length];
  return handler ? handler(values) : { ...DEFAULT_PADDING };
};

/**
 * Convert RGB color to Hex format
 */
export const rgbToHex = (rgb: string): string => {
  if (!rgb || rgb === "transparent" || rgb.startsWith("#")) return rgb || "";
  const match = rgb.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
  if (!match) return rgb;
  const hex = (x: string) => parseInt(x).toString(16).padStart(2, "0");
  return `#${hex(match[1])}${hex(match[2])}${hex(match[3])}`;
};

/**
 * Build padding CSS string from PaddingValues
 */
export const buildPaddingString = (padding: PaddingValues): string => {
  return `${padding.top || 8}px ${padding.right || 12}px ${padding.bottom || 8}px ${padding.left || 12}px`;
};

/**
 * Alignment CSS mapping
 */
const ALIGNMENT_CSS_MAP: Record<string, string> = {
  center: "margin-left: auto; margin-right: auto;",
  right: "margin-left: auto; margin-right: 0;",
  left: "margin-left: 0; margin-right: auto;",
};

/**
 * Get alignment CSS styles
 */
export const getAlignmentCSS = (alignment: string): string => {
  return ALIGNMENT_CSS_MAP[alignment] || ALIGNMENT_CSS_MAP.left;
};

// Common select options for forms
export const PERCENT_OPTIONS = [
  { value: "", label: "Auto" },
  { value: "25", label: "25%" },
  { value: "33", label: "33%" },
  { value: "50", label: "50%" },
  { value: "66", label: "66%" },
  { value: "75", label: "75%" },
  { value: "80", label: "80%" },
  { value: "90", label: "90%" },
  { value: "100", label: "100%" },
];

export const PADDING_OPTIONS = [
  { value: "0", label: "0" },
  { value: "2", label: "2px" },
  { value: "4", label: "4px" },
  { value: "6", label: "6px" },
  { value: "8", label: "8px" },
  { value: "10", label: "10px" },
  { value: "12", label: "12px" },
  { value: "14", label: "14px" },
  { value: "16", label: "16px" },
  { value: "18", label: "18px" },
  { value: "20", label: "20px" },
  { value: "22", label: "22px" },
  { value: "24", label: "24px" },
  { value: "28", label: "28px" },
  { value: "32", label: "32px" },
  { value: "36", label: "36px" },
  { value: "40", label: "40px" },
  { value: "48", label: "48px" },
];

export const FONT_SIZE_OPTIONS = [
  { value: "", label: "Default" },
  { value: "10", label: "10px" },
  { value: "12", label: "12px" },
  { value: "14", label: "14px" },
  { value: "16", label: "16px" },
  { value: "18", label: "18px" },
  { value: "20", label: "20px" },
  { value: "24", label: "24px" },
  { value: "28", label: "28px" },
  { value: "32", label: "32px" },
  { value: "36", label: "36px" },
  { value: "40", label: "40px" },
  { value: "48", label: "48px" },
];

export const FONT_WEIGHT_OPTIONS = [
  { value: "", label: "Default" },
  { value: "300", label: "Light" },
  { value: "400", label: "Normal" },
  { value: "500", label: "Medium" },
  { value: "600", label: "Semi Bold" },
  { value: "700", label: "Bold" },
];

export const QUICK_COLORS = [
  "#ffffff", "#f3f4f6", "#e5e7eb", "#d1d5db",
  "#fee2e2", "#fef3c7", "#d1fae5", "#dbeafe",
  "#e0e7ff", "#fce7f3", "#374151", "#000000",
];

export const BORDER_WIDTH_OPTIONS = [
  { value: "0", label: "0" },
  { value: "1", label: "1px" },
  { value: "2", label: "2px" },
  { value: "3", label: "3px" },
  { value: "4", label: "4px" },
  { value: "5", label: "5px" },
];

/**
 * Generate responsive CSS media query
 */
export const generateMediaQuery = (
  breakpoint: BreakpointKey, 
  selector: string, 
  rules: string
): string => {
  const bp = BREAKPOINTS[breakpoint];
  return `
@media (min-width: ${bp.min}px) ${bp.max !== Infinity ? `and (max-width: ${bp.max}px)` : ""} {
  ${selector} {
    ${rules}
  }
}`;
};

